// src/components/quiz/QuestionInput.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircle2, XCircle, Sparkles, ArrowRight } from 'lucide-react';
import TranslatableText from '../TranslatableText';

export default function QuestionInput({ 
  question, 
  inputText, 
  setInputText, 
  selected, 
  feedback, 
  onSubmit, 
  onKeyDown 
}) {
  const [activeGap, setActiveGap] = useState(0);
  const [isShaking, setIsShaking] = useState(false);
  
  // Detectar si es pregunta de múltiples huecos
  const totalGaps = (question.q.match(/___/g) || []).length;
  const isMultiGap = totalGaps > 1;
  const hasBlanks = question.q.includes('___');

  useEffect(() => {
    // Reset al cambiar pregunta
    setActiveGap(0);
    setIsShaking(false);
    
    // Convertir string a objeto solo si es multi-gap
    if (isMultiGap && typeof inputText === 'string') {
      setInputText({});
    }
  }, [question, isMultiGap]);

  // ✅ MODO MULTI-GAP
  const handleInputChange = (val) => {
    if (isMultiGap) {
      // Auto-salto con espacio
      if (val.endsWith(' ') && val.trim().length > 0 && activeGap < totalGaps - 1) {
        setInputText(prev => ({
          ...prev,
          [activeGap]: val.trim()
        }));
        setActiveGap(prev => prev + 1);
      } else {
        setInputText(prev => ({
          ...prev,
          [activeGap]: val
        }));
      }
    } else {
      // Modo string simple (compatibilidad)
      setInputText(val);
    }
  };

  const handleLocalSubmit = () => {
    if (isMultiGap) {
      // Validar múltiples huecos
      const filledGaps = Object.values(inputText || {}).filter(v => v?.trim().length > 0).length;
      
      if (filledGaps < totalGaps) {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
        return;
      }
    } else {
      // Validar hueco simple
      const value = typeof inputText === 'string' ? inputText : (inputText[0] || '');
      if (value.trim().length === 0) {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
        return;
      }
    }

    onSubmit();
  };

  const renderQuestion = (text) => {
    if (!text.includes('___')) {
      return <h3 className="text-lg md:text-2xl font-black text-slate-800"><TranslatableText text={text} /></h3>;
    }

    const parts = text.split('___');

    return (
      <h3 className="text-lg md:text-2xl font-black text-slate-800 leading-relaxed">
        {parts.map((part, i) => (
          <span key={i}>
            <TranslatableText text={part} /> 
            {i < parts.length - 1 && (() => {
              // ✅ Obtener el valor del hueco actual
              let gapValue = '';
              if (isMultiGap) {
                gapValue = (inputText && inputText[i]) || '';
              } else {
                gapValue = selected || inputText || '';
              }

              // ✅ Manejo de respuestas con "/" (solo mostrar primera opción)
              const displayValue = gapValue ? String(gapValue).split('/')[0] : '...';

              // Determinar si es clickeable o badge
              const isClickable = isMultiGap && !feedback;
              const isActive = activeGap === i;

              return isClickable ? (
                <button
                  type="button"
                  onClick={() => setActiveGap(i)}
                  className={`relative inline-block border-b-4 mx-1 px-3 min-w-[70px] md:min-w-[90px] rounded-t-lg transition-all duration-300 font-bold text-sm md:text-base overflow-hidden
                    ${isActive 
                      ? 'bg-indigo-600 border-indigo-900 text-white scale-110 shadow-xl ring-4 ring-indigo-200' 
                      : gapValue 
                        ? 'bg-indigo-100 border-indigo-400 text-indigo-700 scale-105'
                        : 'border-indigo-300 text-slate-400 bg-slate-50'}
                    ${feedback ? (feedback === 'correct' ? 'border-green-500 bg-green-50 text-green-600' : 'border-red-500 bg-red-50 text-red-600') : ''}
                  `}
                >
                  {/* Shimmer effect en hueco activo */}
                  {isActive && !feedback && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
                  )}
                  
                  <span className="relative z-10">{displayValue}</span>

                  {/* Número de hueco */}
                  {isMultiGap && (
                    <span className={`absolute -top-2 -left-2 w-5 h-5 rounded-full text-xs font-black flex items-center justify-center shadow-md border transition-all ${
                      isActive 
                        ? 'bg-white text-indigo-600 border-indigo-200 scale-110' 
                        : 'bg-slate-100 text-slate-500 border-slate-200'
                    }`}>
                      {i + 1}
                    </span>
                  )}
                </button>
              ) : (
                <span
                  className={`inline-block border-b-4 mx-1 px-2 min-w-[60px] md:min-w-[80px] text-center transition-all duration-300 font-bold text-sm md:text-base rounded-t-lg
                    ${gapValue.length > 0
                      ? (feedback
                        ? (feedback === 'correct' 
                            ? 'border-green-500 text-green-600 bg-green-50 scale-105' 
                            : 'border-red-500 text-red-600 bg-red-50 animate-shake')
                        : 'border-indigo-500 text-indigo-600 bg-indigo-50')
                      : 'border-indigo-300 text-slate-400 bg-slate-50'
                    }`}
                >
                  {displayValue}
                </span>
              );
            })()}
          </span>
        ))}
      </h3>
    );
  };

  // ✅ Obtener valor del input según modo
  const getCurrentInputValue = () => {
    if (feedback) {
      return isMultiGap ? (inputText[activeGap] || '') : (selected || inputText);
    }
    return isMultiGap ? (inputText[activeGap] || '') : inputText;
  };

  // Calcular progreso
  const filledGaps = isMultiGap 
    ? Object.values(inputText || {}).filter(v => v?.trim().length > 0).length 
    : (getCurrentInputValue()?.trim().length > 0 ? 1 : 0);

  return (
    <div className="animate-in fade-in duration-500">
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-shake { animation: shake 0.4s ease-in-out; }
        .animate-shimmer { animation: shimmer 2s infinite; }
      `}</style>

      {/* TARJETA DE PREGUNTA CON FEEDBACK */}
      <div className={`bg-white p-4 md:p-8 rounded-[2rem] shadow-sm border-2 min-h-[120px] md:min-h-[180px] flex flex-col items-center justify-center text-center mb-4 transition-all relative ${
        isShaking 
          ? 'animate-shake border-red-300 bg-red-50' 
          : feedback === 'correct'
            ? 'border-green-300 bg-green-50/30'
            : feedback === 'wrong'
              ? 'border-red-300 bg-red-50/30'
              : 'border-slate-100'
      }`}>
        
        {/* Icono de feedback */}
        {feedback === 'correct' && (
          <CheckCircle2 
            className="absolute top-2 right-2 md:top-4 md:right-4 text-green-500 animate-in zoom-in" 
            size={24} 
          />
        )}
        {feedback === 'wrong' && (
          <XCircle 
            className="absolute top-2 right-2 md:top-4 md:right-4 text-red-500 animate-in zoom-in" 
            size={24} 
          />
        )}

        {/* Etiqueta superior */}
        <span className={`text-[10px] font-black uppercase tracking-widest mb-4 block transition-colors ${
          isShaking 
            ? 'text-red-500' 
            : isMultiGap 
              ? 'text-indigo-400'
              : 'text-indigo-300'
        }`}>
          {isShaking 
            ? '¡Completa todos los huecos!' 
            : (isMultiGap 
                ? `HUECO ${activeGap + 1} DE ${totalGaps}` 
                : 'ESCRIBE TU RESPUESTA')}
        </span>

        {renderQuestion(question.q)}

        {/* Contador de progreso para multi-gap */}
        {isMultiGap && hasBlanks && (
          <div className="mt-4 flex items-center justify-center gap-2">
            {[...Array(totalGaps)].map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i < filledGaps 
                    ? 'w-8 bg-indigo-500 scale-110' 
                    : 'w-6 bg-slate-200'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* MENSAJE CONTEXTUAL */}
      {!feedback && isMultiGap && (
        <div className="text-center mb-3 animate-in fade-in slide-in-from-top-2 duration-300">
          <p className="text-xs font-bold text-indigo-600 bg-indigo-50 py-2 px-4 rounded-xl inline-flex items-center gap-2">
            <Sparkles size={12} />
            Presiona ESPACIO para ir al siguiente hueco
            <ArrowRight size={12} />
          </p>
        </div>
      )}

      {/* INPUT PRINCIPAL CON EFECTOS */}
      <div className="space-y-3 md:space-y-4">
        <div className="relative">
          <input
            type="text"
            value={getCurrentInputValue()}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !feedback ? handleLocalSubmit() : onKeyDown(e)}
            disabled={!!feedback}
            autoFocus
            placeholder={isMultiGap ? `Escribe la palabra ${activeGap + 1}...` : "Escribe aquí..."}
            className={`w-full p-3 md:p-5 text-center text-base md:text-xl font-bold border-2 rounded-xl md:rounded-2xl outline-none shadow-sm transition-all ${
              feedback
                ? (feedback === 'correct' 
                    ? 'border-green-500 bg-green-50 text-green-700 ring-4 ring-green-100' 
                    : 'border-red-500 bg-red-50 text-red-700 ring-4 ring-red-100')
                : 'border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100'
            }`}
          />
          
          {/* Indicador de caracteres */}
          {!feedback && getCurrentInputValue()?.length > 0 && (
            <span className="absolute bottom-2 right-3 text-xs font-bold text-slate-400">
              {getCurrentInputValue()?.length} caracteres
            </span>
          )}
        </div>

        {/* Hint de progreso */}
        {!feedback && isMultiGap && filledGaps < totalGaps && filledGaps > 0 && (
          <div className="text-center animate-in fade-in">
            <p className="text-xs font-bold text-slate-500">
              {filledGaps} de {totalGaps} huecos completados
            </p>
          </div>
        )}

        {/* BOTÓN COMPROBAR MEJORADO */}
        {!feedback && (
          <button
            onClick={handleLocalSubmit}
            disabled={filledGaps === 0}
            className={`w-full py-3 md:py-4 rounded-2xl font-black text-base md:text-lg border-b-8 shadow-xl transition-all flex items-center justify-center gap-2 ${
              filledGaps > 0
                ? 'bg-indigo-600 text-white border-indigo-900 shadow-indigo-100 hover:bg-indigo-500 active:border-b-0 active:translate-y-2'
                : 'bg-slate-300 text-slate-500 border-slate-400 cursor-not-allowed opacity-50'
            }`}
          >
            COMPROBAR
            {filledGaps >= totalGaps && <CheckCircle2 size={20} />}
          </button>
        )}
      </div>
    </div>
  );
}
